import { createReducer, on } from '@ngrx/store';
import { AccountsActions } from './account.actions';
import { Account, AvailibleEquipment, LinkedDevice, Parameter, ScheduledAction, } from '../../../shared/interfaces/account.interfaces';
import { Action, Device } from '../../../shared/interfaces/click.interfaces';
import { ACMRemoteAssignment } from '../../../shared/interfaces/equipment.interfaces';

export const accountsFeatureKey = 'accounts';

export interface AccountsState {
  count?: number | undefined;
  accounts?: Account[] | [] | undefined;
  selectedAccount?: Account | undefined;
  filter?: string | undefined;
  pageIndex?: number | undefined;
  sort?: {
    sortField: string | undefined;
    sortOrder: 1 | -1 | undefined;
  }
  availibleEquipments?: AvailibleEquipment[] | undefined;
  linkedDevices?: Device[] | undefined;
  selectedLinkedDevice?: LinkedDevice | undefined;
  foundDevice?: Device | [] | undefined;
  searchError?: any | undefined;
  zones?: Parameter[] | [] | undefined;
  users?: Parameter[] | [] | undefined;
  scheduledActions?: ScheduledAction[] | [] | undefined;
  selectedScheduledAction?: ScheduledAction | undefined;
  accountACMRemoteAssignments?: ACMRemoteAssignment[] | undefined;
}

export const initialState: AccountsState = {};

export const accountsReducer = createReducer(
  initialState,

  on(AccountsActions.setFilter, (state, action) => ({ ...state, filter: action.filter })),
  on(AccountsActions.setPageIndex, (state, action) => ({ ...state, pageIndex: action.pageIndex })),
  on(AccountsActions.setSort, (state, action) => ({ ...state, sort: action.sort })),

  on(AccountsActions.loadAccountsSuccess, (state, action) => ({ ...state, count: action.data.count, accounts: action.data.accounts })),

  on(AccountsActions.loadAccountByIdSuccess, (state, action) => ({ ...state, selectedAccount: action.data })),

  on(AccountsActions.updateAccountSuccess, (state, action) => {
    if (!state.selectedAccount) {
      return state;
    }
    return {
      ...state,
      selectedAccount: {
        ...state.selectedAccount,
        name: action.data.name ?? state.selectedAccount.name,
        number: action.data.number ?? state.selectedAccount.number,
        partition: action.data.partition ?? state.selectedAccount.partition,
      },
    };
  }),

  on(AccountsActions.unsetSelectedAccount, (state) => ({ ...state, selectedAccount: undefined })),

  on(AccountsActions.loadAvailibleEquipmentsSuccess, (state, action) => ({ ...state, availibleEquipments: action.data })),

  on(AccountsActions.loadAccountLinkedDevicesSuccess, (state, action) => {
    const linkedDevices = Object.entries(action.data ?? {}).map(
      ([uid, value]: [string, any]) => ({
        uid,
        email: value?.email || 'no email',
        name: value?.name || 'no name',
      })
    );
    return { ...state, linkedDevices }
  }),

  on(AccountsActions.searchDeviceSuccess, (state, action) => ({ ...state, foundDevice: action.data, searchError: undefined })),
  on(AccountsActions.searchDeviceFailure, (state, action) => ({ ...state, searchError: action.error, foundDevice: undefined })),
  on(AccountsActions.unsetDeviceNotFoundError, (state) => ({ ...state, searchError: undefined })),

  on(AccountsActions.unsetFoundDevice, (state) => ({ ...state, foundDevice: undefined })),

  on(AccountsActions.linkDeviceSuccess, (state, action) => {
    const linkedDevices = Object.entries(action.data ?? {}).map(
      ([uid, value]: [string, any]) => ({
        uid,
        email: value?.email || 'no email',
        name: value?.name || 'no name',
      })
    );
    return { ...state, linkedDevices };
  }),

  on(AccountsActions.loadLinkedDeviceByFirebaseIdSuccess, (state, action) => {
    if (!state.linkedDevices) return state;

    // Buscar info previa de ese device
    const deviceInfo = state.linkedDevices.find(
      (ld) => ld.uid === action.firebaseUid
    ) || { email: '', name: '' };

    const linkedDevice = {
      uid: action.firebaseUid,
      email: deviceInfo.email,
      name: deviceInfo.name,
      template: {
        commands: Object.values(action.data?.commands ?? {}).map((cmd: any) => {
          let actionsArr = [];
          try {
            actionsArr =
              typeof cmd.command === 'string' ? JSON.parse(cmd.command) : [];
          } catch {
            actionsArr = [];
          }
          return {
            name: cmd.name,
            icon_color: cmd.icon?.color,
            icon_type: cmd.icon?.type,
            icon: cmd.icon?.icon,
            description: cmd.description || '',
            actions: actionsArr as Action[],
          };
        }),
        keypadEnabled: action.data?.keypadEnabled ?? false,
        logEnabled: action.data?.logEnabled ?? false,
        notifications: action.data?.notifications ?? [],
      },
    };

    return { ...state, selectedLinkedDevice: linkedDevice }
  }),

  on(AccountsActions.loadAccountParametersSuccess, (state, action) => {
    const users = (action.data ?? []).filter((param: Parameter) => param.parameter_types_id === 1);
    const zones = (action.data ?? []).filter((param: Parameter) => param.parameter_types_id === 2);
    return {
      ...state,
      users,
      zones,
    };
  }),

  on(AccountsActions.loadScheduledActionsSuccess, (state, action) => ({ ...state, scheduledActions: action.data })),
  on(AccountsActions.loadScheduledActionByIdSuccess, (state, action) => ({ ...state, selectedScheduledAction: action.data })),

  on(AccountsActions.loadACMRemoteAssignmentsByAccountIdSuccess, (state, action) => ({ ...state, accountACMRemoteAssignments: action.data })),

  on(AccountsActions.unsetAccountsStore, () => initialState)
);
